import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Pagination
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import SearchIcon from '@mui/icons-material/Search';

interface HaciogluProduct {
  id: number;
  stokKodu: number;
  stokAdi: string;
  urunGurubu?: string;
  aktif: boolean;
  fiyat?: number;
}

interface Props {
  loading: boolean;
  products: HaciogluProduct[];
  onAddProduct: (product: { stokKodu: number; stokAdi: string; urunGurubu?: string; fiyat?: number }) => Promise<void>;
  onUpdateProduct: (id: number, updates: { stokAdi?: string; urunGurubu?: string; fiyat?: number }) => Promise<void>;
  onDeactivateProduct: (product: HaciogluProduct) => Promise<void>;
}

const HaciogluProductManagement: React.FC<Props> = ({
  loading,
  products,
  onAddProduct,
  onUpdateProduct,
  onDeactivateProduct
}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<HaciogluProduct | null>(null);
  const [searchText, setSearchText] = useState('');
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;
  
  const [newProduct, setNewProduct] = useState({
    stokKodu: 0,
    stokAdi: '',
    urunGurubu: '',
    fiyat: 0
  });

  // Arama filtresi
  const filteredProducts = products.filter(p => 
    p.stokAdi.toLowerCase().includes(searchText.toLowerCase()) ||
    p.stokKodu.toString().includes(searchText) ||
    (p.urunGurubu && p.urunGurubu.toLowerCase().includes(searchText.toLowerCase()))
  );

  // Sayfalama
  const paginatedProducts = filteredProducts.slice((page - 1) * itemsPerPage, page * itemsPerPage);

  const handleAddClick = async () => {
    await onAddProduct(newProduct);
    setAddDialogOpen(false);
    setNewProduct({ stokKodu: 0, stokAdi: '', urunGurubu: '', fiyat: 0 });
  };

  const handleUpdateClick = async () => {
    if (!selectedProduct) return;
    await onUpdateProduct(selectedProduct.id, {
      stokAdi: selectedProduct.stokAdi,
      urunGurubu: selectedProduct.urunGurubu,
      fiyat: selectedProduct.fiyat
    });
    setEditDialogOpen(false);
    setSelectedProduct(null);
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Hacioglu Product Management ({filteredProducts.length})
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
          color="primary"
        >
          Add New Product
        </Button>
      </Box>

      {/* Arama Alanı */}
      <Box mb={2}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search by product name, stock code, or product group..."
          value={searchText}
          onChange={(e) => {
            setSearchText(e.target.value);
            setPage(1); // Arama yapınca sayfayı sıfırla
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Stock Code</TableCell>
                  <TableCell>Product Name</TableCell>
                  <TableCell>Product Group</TableCell>
                  <TableCell>Price</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProducts.map((product) => (
                  <TableRow key={product.id}>
                    <TableCell>{product.stokKodu}</TableCell>
                    <TableCell>{product.stokAdi}</TableCell>
                    <TableCell>{product.urunGurubu || '-'}</TableCell>
                    <TableCell>{product.fiyat ? `${product.fiyat} ₺` : '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={product.aktif ? 'Active' : 'Passive'}
                        color={product.aktif ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSelectedProduct(product);
                            setEditDialogOpen(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      {product.aktif && (
                        <Tooltip title="Set Passive">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => onDeactivateProduct(product)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {paginatedProducts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary" py={3}>
                        {searchText ? 'No search results found.' : 'No products yet. Click the button above to add a new product.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Sayfalama */}
          {filteredProducts.length > itemsPerPage && (
            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={Math.ceil(filteredProducts.length / itemsPerPage)}
                page={page}
                onChange={(e, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Yeni Ürün Ekleme Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Product</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Stock Code"
              type="number"
              value={newProduct.stokKodu || ''}
              onChange={(e) => setNewProduct({ ...newProduct, stokKodu: parseInt(e.target.value) || 0 })}
              required
              fullWidth
            />
            <TextField
              label="Product Name"
              value={newProduct.stokAdi}
              onChange={(e) => setNewProduct({ ...newProduct, stokAdi: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Product Group"
              value={newProduct.urunGurubu}
              onChange={(e) => setNewProduct({ ...newProduct, urunGurubu: e.target.value })}
              fullWidth
            />
            <TextField
              label="Price (₺)"
              type="number"
              value={newProduct.fiyat || ''}
              onChange={(e) => setNewProduct({ ...newProduct, fiyat: parseFloat(e.target.value) || 0 })}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddClick} variant="contained" disabled={loading}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ürün Düzenleme Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Product</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Box display="flex" flexDirection="column" gap={2} mt={1}>
              <TextField
                label="Stock Code"
                value={selectedProduct.stokKodu}
                disabled
                fullWidth
              />
              <TextField
                label="Product Name"
                value={selectedProduct.stokAdi}
                onChange={(e) => setSelectedProduct({ ...selectedProduct, stokAdi: e.target.value })}
                fullWidth
              />
              <TextField
                label="Product Group"
                value={selectedProduct.urunGurubu || ''}
                onChange={(e) => setSelectedProduct({ ...selectedProduct, urunGurubu: e.target.value })}
                fullWidth
              />
              <TextField
                label="Price (₺)"
                type="number"
                value={selectedProduct.fiyat || ''}
                onChange={(e) => setSelectedProduct({ ...selectedProduct, fiyat: parseFloat(e.target.value) || 0 })}
                fullWidth
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleUpdateClick} variant="contained" disabled={loading}>
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default HaciogluProductManagement;

